import {isStringTemplate} from '@frontapp/workflows/expressions';
import {
  buildStringComparatorParameter,
  splitStringComparatorParameter,
} from '@frontapp/workflows/rules/parameters';
import {isBoolean, isNumber, isString} from 'lodash';
import {DateTime} from 'luxon';
import React, {ClipboardEventHandler, FC, ReactNode, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {
  defineMessage,
  MessageDescriptor,
  Trans,
  useI18n,
} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {InboxModel} from '../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {inboxHasColor} from '../../../libs/shared/core/src/models/helpers/inboxHelpers';
import {buildTeammateDisplayName} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {getTimeFormat} from '../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {TeammateAvatar} from '../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {BoxedInput, StyledBoxDiv} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {DropdownBoxCoordinator} from '../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {NumberInputBox} from '../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {AbsoluteDateTimeFormatter} from '../../../libs/shared/ui/src/components/datetime/absoluteDateTimeFormatter';
import {Dropdown} from '../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppSelector} from '../../helpers/appReduxHelpers';
import {AnswerModel} from '../../models/api/answerModel';
import {CircleModel, CirclePublicId, isCirclePublicId} from '../../models/api/circleModel';
import {DataFieldsTableColumnModel, DataFieldType} from '../../models/api/dataFieldsTableColumnModel';
import {dataFieldsTableListValueSeparator} from '../../models/api/dataFieldsTableModel';
import {TagModel} from '../../models/api/tagModel';
import {buildPublicId, tryParsePublicId} from '../../models/applications/publicIdTypes';
import {EnumParameter as EnumDefinition} from '../../settings/editors/commons/parameterModel';
import {isExpression} from '../../settings/editors/expressions/expressionModel';
import {isExpressionRef} from '../../settings/editors/expressions/expressionRefModel';
import {useFetchAllCircles} from '../../settings/fetchers/circles';
import {InputSplitStringDropdown} from '../../settings/rules/ruleEditor/inputs/inputSplitStringDropdown';
import {AnswerParameter} from '../../settings/rules/ruleEditor/parameters/answerParameter';
import {BooleanDropdownParameter} from '../../settings/rules/ruleEditor/parameters/booleanDropdownParameter';
import {EnumParameter} from '../../settings/rules/ruleEditor/parameters/enumParameter';
import {InboxesParameter, InboxParameter} from '../../settings/rules/ruleEditor/parameters/inboxParameter';
import {LanguageParameter} from '../../settings/rules/ruleEditor/parameters/languageParameter';
import {TagParameter, TagsParameter} from '../../settings/rules/ruleEditor/parameters/tagParameter';
import {
  TeammateParameter,
  TeammatesParameter,
} from '../../settings/rules/ruleEditor/parameters/teammateParameter';
import {DateTimePicker} from '../common/dateTime/dateTimePicker';
import {renderTagIcon} from '../common/dropdowns/dropdownItemTagIcon';
import {OverflowWithCount} from '../common/overflow/overflowWithCount';
import {DataFieldsTableSearchValue, useDataFieldsTableSearchContext} from './dataFieldsTableSearchContext';
import {
  deserializeDataFieldsTableResourceListValue,
  deserializeDataFieldsTableResourceValue,
  serializeDataFieldsTableResourceListValue,
  serializeDataFieldsTableResourceValue,
} from './helpers';

/*
 * Props.
 */

export interface DataFieldsTableCellProps {
  column: DataFieldsTableColumnModel;
  listSeparator?: ReactNode;
  value: string | undefined;
  onChange: (arg: string | undefined) => void;
  hasSplitStringInput?: boolean;
  parameterName?: string;
  enumDefinition?: EnumDefinition;
  placeholder?: MessageDescriptor;
  isDisabled?: boolean;
}

/*
 * Styles.
 */

const StyledSplitStringPart = styled.div`
  padding: 4px 0;
  border-radius: 4px;
`;

const StyledBoxedInput = styled(BoxedInput)`
  width: 100%;
  ${StyledBoxDiv} {
    background-color: transparent;
    & > input {
      text-overflow: ellipsis;
    }
  }

  &:hover {
    ${StyledSplitStringPart} {
      background-color: ${(p) => p.theme.greys.shade30};
    }
  }
`;

const StyledBoxedNumberInput = styled(NumberInputBox)`
  width: 100%;
  ${StyledBoxDiv} {
    background-color: transparent;

    :hover:not(:focus-within) {
      background-color: ${(p) => p.theme.greys.shade20};
    }
  }
`;

const StyledLabel = styled.div<{$isPlaceholder: boolean}>`
  width: 100%;
  padding-left: 8px;
  height: 26px;
  line-height: 26px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};

  color: ${(p) => (p.$isPlaceholder ? p.theme.greys.shade60 : p.theme.greys.shade80)};
  ${ellipsis()}
`;

const StyledResourceLabelWrapper = styled(StyledLabel)`
  ${flexCenter};
`;

const StyledResourceItemWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  gap: 4px;
`;

const StyledTextDiv = styled.div`
  align-self: center;
  line-height: 18px;
  flex-shrink: 1;
  ${ellipsis()}
`;

// Workaround for issue with styled compnents not passing through generic type correctly
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const StyledOverflowWithCount = styled(OverflowWithCount)`
  align-items: center;
` as typeof OverflowWithCount;

/*
 * Component.
 */

export const DataFieldsTableCellRenderer: FC<DataFieldsTableCellProps> = (props) => {
  const CellRenderer = CELL_RENDERERS[props.column.type];
  if (!CellRenderer) {
    reportToBugsnag(new Error(`Missing data fields table renderer for type: ${props.column.type}`));
    return <span>Missing renderer for type: {props.column.type}</span>;
  }
  return <CellRenderer {...props} />;
};

const TextRenderer: FC<DataFieldsTableCellProps> = (props) => {
  const {
    value: initialValue,
    onChange,
    hasSplitStringInput,
    listSeparator: separator,
    enumDefinition,
    isDisabled,
  } = props;
  const {i18n} = useI18n();
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState(initialValue);
  // If the initialValue is changed externally, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlur = () => {
    if (value !== initialValue) {
      onChange(value);
    }
  };

  const placeholder = i18n._(
    props.placeholder ??
      defineMessage({
        id: 'rule-editor-data-table-split-text-cell-placeholder',
        message: 'Enter another set of keywords',
      }),
  );

  if (hasSplitStringInput) {
    const tokens = splitStringComparatorParameter(value ?? '');
    const onChangeTokens = (newTokens: ReadonlyArray<string>) => {
      onChange(buildStringComparatorParameter(newTokens));
    };

    const onPaste: ClipboardEventHandler = (event) => {
      event.preventDefault();
      const clipboardData = event.clipboardData.getData('text/plain');
      const newTokens = clipboardData.split(/\n/).flatMap((part) => splitStringComparatorParameter(part));
      onChangeTokens([...tokens, ...newTokens]);
    };

    return (
      <DropdownBoxCoordinator
        shouldDisablePadding
        noMargin
        renderButton={(isOpen) => (
          <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
            {() => (
              <StyledResourceLabelWrapper $isPlaceholder={!tokens?.length}>
                {value ? (
                  <StyledOverflowWithCount
                    elements={tokens}
                    renderSeparator={() => <StyledTextDiv>{separator}</StyledTextDiv>}
                    listDivStyles={{display: 'flex', gap: '6px'}}
                    renderElement={(token) => <StyledSplitStringPart>{token}</StyledSplitStringPart>}
                    renderOverflowIndicator={overflowIndicatorRenderer}
                  />
                ) : (
                  placeholder
                )}
              </StyledResourceLabelWrapper>
            )}
          </StyledBoxedInput>
        )}
        renderDropdown={(dropdownProps) => (
          <InputSplitStringDropdown
            {...dropdownProps}
            width={300}
            description={
              tokens.length > 0 ? (
                <Trans id="data-fields-table-cell-text-input-split-description">
                  If any of the keywords match
                </Trans>
              ) : undefined
            }
            placeholder={placeholder}
            tokens={tokens}
            onChange={onChangeTokens}
            delimiters={[Key.Enter, Key.Tab, ';']}
            onPaste={onPaste}
          />
        )}
      />
    );
  }

  if (enumDefinition) {
    const renderPlaceholder = () =>
      props.placeholder ? (
        i18n._(props.placeholder)
      ) : (
        <Trans id="workflow-editor-table-enum-cell-default-placeholder">Select a value</Trans>
      );

    return (
      <EnumParameter
        enumParameter={enumDefinition}
        selectedItem={value && enumDefinition.enum.includes(value) ? value : null}
        onChange={(selected) =>
          isString(selected) && enumDefinition.enum.includes(selected)
            ? onChange(selected)
            : onChange(undefined)
        }
        renderButton={(selectedItem, isOpen) => (
          <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
            {() => (
              <StyledResourceLabelWrapper $isPlaceholder={!selectedItem}>
                {selectedItem && !isExpression(selectedItem)
                  ? renderEnumName(selectedItem.name)
                  : renderPlaceholder()}
              </StyledResourceLabelWrapper>
            )}
          </StyledBoxedInput>
        )}
      />
    );
  }

  return (
    <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL}>
      {(childProps) => (
        <BoxedInputElement
          {...childProps}
          isDisabled={isDisabled}
          placeholder={placeholder}
          onBlur={onBlur}
          onKeyDown={(event) => {
            if (event.key === Key.Enter && !event.shiftKey) {
              onBlur();
            }
          }}
          type={BoxedInputElementTypesEnum.TEXT}
          value={value}
          onChange={(v) => setValue(v)}
        />
      )}
    </StyledBoxedInput>
  );
};

const BooleanRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => (
  <BooleanDropdownParameter
    buttonWidth="100%"
    selectedItem={value ? value === 'true' : null}
    onChange={(boolean) => (isBoolean(boolean) ? onChange(boolean.toString()) : onChange(undefined))}
    renderButton={(selectedValue, isOpen) => (
      <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
        {() => (
          <StyledResourceLabelWrapper $isPlaceholder={!selectedValue}>
            {selectedValue && !isExpression(selectedValue) ? (
              selectedValue.label
            ) : (
              <Trans id="rule-editor-data-table-boolean-cell-placeholder">Select a value</Trans>
            )}
          </StyledResourceLabelWrapper>
        )}
      </StyledBoxedInput>
    )}
    isSearchDisabled
  />
);

const CannedAnswerRenderer: FC<DataFieldsTableCellProps> = ({value: encodedValue, onChange}) => {
  const {addSearchData} = useDataFieldsTableSearchContext();
  const cannedAnswerId = deserializeDataFieldsTableResourceValue(encodedValue, 'cannedAnswer');

  return (
    <AnswerParameter
      buttonWidth="100%"
      onFetchSelectedItems={(answers) => addSearchData(answers.map(cannedAnswerToSearchValue))}
      renderButton={(selectedAnswer, isOpen) => (
        <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
          {() => (
            <StyledResourceLabelWrapper $isPlaceholder={!selectedAnswer}>
              {selectedAnswer && !isExpression(selectedAnswer) ? (
                renderCannedAnswerValue(selectedAnswer)
              ) : (
                <Trans id="rule-editor-data-table-canned-answer-cell-placeholder">Select a template</Trans>
              )}
            </StyledResourceLabelWrapper>
          )}
        </StyledBoxedInput>
      )}
      selectedItem={cannedAnswerId ?? null}
      onChange={(value) => {
        if (isExpressionRef(value)) {
          return;
        }
        onChange(value ? buildPublicId('cannedAnswer', value) : undefined);
      }}
    />
  );
};

const NumberRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const {i18n} = useI18n();

  return (
    <StyledBoxedNumberInput
      value={isFinite(Number(value)) ? Number(value) : ''}
      placeholder={i18n._(
        defineMessage({
          id: 'rule-editor-data-table-number-cell-placeholder',
          message: 'Enter a number',
        }),
      )}
      onChange={(number) => {
        onChange(number?.toString() ?? undefined);
      }}
      noMargin
      size={VisualSizesEnum.SMALL}
    />
  );
};

const DatetimeRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const datetime = value ? DateTime.fromISO(value) : undefined;
  const timeFormat = useAppSelector(getTimeFormat);

  return (
    <DropdownBoxCoordinator
      shouldDisablePadding
      noMargin
      renderButton={(isOpen) => (
        <StyledLabel $isPlaceholder={!datetime}>
          {datetime ? (
            <AbsoluteDateTimeFormatter value={datetime.toMillis()} timeFormat={timeFormat} showYear />
          ) : (
            <Trans id="rule-editor-data-table-datetime-cell-placeholder">Select a value</Trans>
          )}
        </StyledLabel>
      )}
      renderDropdown={(dropdownProps) => (
        <Dropdown {...dropdownProps}>
          <DateTimePicker
            selectedDateTime={datetime}
            timeFormat={timeFormat}
            onDateTimeSelect={(newValue) => onChange(newValue?.toISO() ?? undefined)}
          />
        </Dropdown>
      )}
    />
  );
};

const TagRenderer: FC<DataFieldsTableCellProps> = ({value: encodedValue, onChange}) => {
  const {addSearchData} = useDataFieldsTableSearchContext();
  const tagId = deserializeDataFieldsTableResourceValue(encodedValue, 'tag');

  return (
    <TagParameter
      buttonWidth="100%"
      onFetchSelectedItems={(tags) => addSearchData(tags.map(tagToSearchValue))}
      renderButton={(selectedTag, isOpen) => (
        <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
          {() => (
            <StyledResourceLabelWrapper $isPlaceholder={!selectedTag}>
              {selectedTag && !isExpression(selectedTag) ? (
                renderTagValue(selectedTag)
              ) : (
                <Trans id="rule-editor-data-table-tag-cell-placeholder">Select a tag</Trans>
              )}
            </StyledResourceLabelWrapper>
          )}
        </StyledBoxedInput>
      )}
      selectedItem={tagId ?? null}
      onChange={(value) => {
        if (isExpressionRef(value)) {
          return;
        }
        onChange(value ? buildPublicId('tag', value) : undefined);
      }}
    />
  );
};

const TagListRenderer: FC<DataFieldsTableCellProps> = ({value, onChange, listSeparator: separator}) => {
  const {addSearchData} = useDataFieldsTableSearchContext();
  const tagIds = deserializeDataFieldsTableResourceListValue(value, 'tag');

  return (
    <TagsParameter
      buttonWidth="100%"
      onFetchSelectedItems={(tags) => addSearchData(tags.map(tagToSearchValue))}
      renderButton={(selectedTagsAndExpressions, isOpen) => {
        const selectedTags = selectedTagsAndExpressions?.filter((tag): tag is TagModel => !isExpression(tag));
        return (
          <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
            {() => (
              <StyledResourceLabelWrapper $isPlaceholder={!selectedTags?.length}>
                {selectedTags?.length ? (
                  <StyledOverflowWithCount
                    elements={selectedTags}
                    renderSeparator={() => <StyledTextDiv>{separator}</StyledTextDiv>}
                    listDivStyles={{display: 'flex', gap: '6px'}}
                    renderElement={(tag) => renderTagValue(tag)}
                    renderOverflowIndicator={overflowIndicatorRenderer}
                  />
                ) : (
                  <Trans id="rule-editor-data-table-tag-list-cell-placeholder">Select tags</Trans>
                )}
              </StyledResourceLabelWrapper>
            )}
          </StyledBoxedInput>
        );
      }}
      selectedItems={tagIds ?? null}
      onChange={(newSelectedTags) => {
        onChange(serializeDataFieldsTableResourceListValue(newSelectedTags?.filter(isNumber), 'tag'));
      }}
      isResourceCreationAllowed
    />
  );
};

const InboxRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const inboxId = deserializeDataFieldsTableResourceValue(value, 'inbox');
  const {addSearchData} = useDataFieldsTableSearchContext();

  return (
    <InboxParameter
      showOtherNamespaceInboxes
      selectedItem={inboxId ?? null}
      buttonWidth="100%"
      onFetchSelectedItems={(inboxes) => addSearchData(inboxes.map(inboxToSearchValue))}
      renderButton={(selectedInbox, isOpen) => (
        <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
          {() => (
            <StyledResourceLabelWrapper $isPlaceholder={!selectedInbox}>
              {selectedInbox && !isExpression(selectedInbox) ? (
                renderInboxValue(selectedInbox)
              ) : (
                <Trans id="rule-editor-data-table-inbox-cell-placeholder">Select an inbox</Trans>
              )}
            </StyledResourceLabelWrapper>
          )}
        </StyledBoxedInput>
      )}
      onChange={(newInboxId) => {
        if (isExpressionRef(newInboxId)) {
          return;
        }
        onChange(serializeDataFieldsTableResourceValue(newInboxId, 'inbox'));
      }}
    />
  );
};

const InboxListRenderer: FC<DataFieldsTableCellProps> = ({value, onChange, listSeparator: separator}) => {
  const inboxIds = deserializeDataFieldsTableResourceListValue(value, 'inbox');
  const {addSearchData} = useDataFieldsTableSearchContext();

  return (
    <InboxesParameter
      showOtherNamespaceInboxes
      buttonWidth="100%"
      onFetchSelectedItems={(inboxes) => addSearchData(inboxes.map(inboxToSearchValue))}
      renderButton={(selectedInboxesAndExpressions, isOpen) => {
        const selectedInboxes = selectedInboxesAndExpressions?.filter(
          (inbox): inbox is InboxModel => !isExpression(inbox),
        );
        return (
          <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
            {() => (
              <StyledResourceLabelWrapper $isPlaceholder={!selectedInboxes?.length}>
                {selectedInboxes?.length ? (
                  <StyledOverflowWithCount
                    elements={selectedInboxes}
                    renderSeparator={() => <StyledTextDiv>{separator}</StyledTextDiv>}
                    listDivStyles={{display: 'flex', gap: '6px'}}
                    renderElement={(inbox) => renderInboxValue(inbox)}
                    renderOverflowIndicator={overflowIndicatorRenderer}
                  />
                ) : (
                  <Trans id="rule-editor-data-table-inbox-list-cell-placeholder">Select inboxes</Trans>
                )}
              </StyledResourceLabelWrapper>
            )}
          </StyledBoxedInput>
        );
      }}
      selectedItems={inboxIds ?? null}
      onChange={(newSelectedInboxes) => {
        onChange(serializeDataFieldsTableResourceListValue(newSelectedInboxes?.filter(isNumber), 'inbox'));
      }}
    />
  );
};

const LanguageRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => (
  <LanguageParameter
    buttonWidth="100%"
    selectedItem={value ?? null}
    onChange={(selected) => {
      if (isStringTemplate(selected)) {
        return;
      }
      onChange(selected ?? undefined);
    }}
    renderButton={(selectedItem, isOpen) => (
      <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
        {() => (
          <StyledResourceLabelWrapper $isPlaceholder={!selectedItem}>
            {selectedItem && !isExpression(selectedItem) ? (
              renderEnumName(selectedItem.name)
            ) : (
              <Trans id="rule-editor-data-table-languge-cell-placeholder">Select a language</Trans>
            )}
          </StyledResourceLabelWrapper>
        )}
      </StyledBoxedInput>
    )}
  />
);

const TeammateRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const teammateId = deserializeDataFieldsTableResourceValue(value, 'teammate');
  const {addSearchData} = useDataFieldsTableSearchContext();

  return (
    <TeammateParameter
      buttonWidth="100%"
      onFetchSelectedItems={(teammates) => addSearchData(teammates.map(teammateOrCircleToSearchValue))}
      renderButton={(selectedTeammate, isOpen) => (
        <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
          {() => (
            <StyledResourceLabelWrapper $isPlaceholder={!selectedTeammate}>
              {selectedTeammate && !isExpression(selectedTeammate) ? (
                renderTeammateValue(selectedTeammate)
              ) : (
                <Trans id="rule-editor-data-table-teammate-cell-placeholder">Select a teammate</Trans>
              )}
            </StyledResourceLabelWrapper>
          )}
        </StyledBoxedInput>
      )}
      selectedItem={teammateId ?? null}
      onChange={(newTeammateId) => {
        if (isExpressionRef(newTeammateId)) {
          return;
        }
        onChange(serializeDataFieldsTableResourceValue(newTeammateId, 'teammate'));
      }}
      shouldShowAllInitiallySelectedTeammates
    />
  );
};

const TeammateListRenderer: FC<DataFieldsTableCellProps> = ({value, onChange, listSeparator: separator}) => {
  const {addSearchData} = useDataFieldsTableSearchContext();
  const teammateAndCircleIds =
    value
      ?.split(dataFieldsTableListValueSeparator)
      .map((teammateOrCircleId) => {
        if (isCirclePublicId(teammateOrCircleId)) {
          return teammateOrCircleId;
        }
        return tryParsePublicId(teammateOrCircleId, 'teammate');
      })
      .filter(isDefined) ?? null;

  const {circles} = useFetchAllCircles();

  return (
    <TeammatesParameter
      buttonWidth="100%"
      onFetchSelectedItems={(teammates) => addSearchData(teammates.map(teammateOrCircleToSearchValue))}
      renderButton={(selectedTeammatesAndCirclesAndExpressions, isOpen) => {
        const selectedTeammatesAndCircles = selectedTeammatesAndCirclesAndExpressions?.filter(
          (teammateOrCircle): teammateOrCircle is CirclePublicId | TeammateModel =>
            !isExpression(teammateOrCircle),
        );

        return (
          <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
            {() => (
              <StyledResourceLabelWrapper $isPlaceholder={!selectedTeammatesAndCircles?.length}>
                {selectedTeammatesAndCircles?.length ? (
                  <StyledOverflowWithCount
                    elements={selectedTeammatesAndCircles}
                    renderSeparator={() => <StyledTextDiv>{separator}</StyledTextDiv>}
                    listDivStyles={{display: 'flex', gap: '6px'}}
                    renderElement={(teammateOrCircle) =>
                      renderTeammateOrCircleValue(teammateOrCircle, circles)
                    }
                    renderOverflowIndicator={overflowIndicatorRenderer}
                  />
                ) : (
                  <Trans id="rule-editor-data-table-teammate-list-cell-placeholder">Select teammates</Trans>
                )}
              </StyledResourceLabelWrapper>
            )}
          </StyledBoxedInput>
        );
      }}
      selectedItems={teammateAndCircleIds}
      onChange={(newSelectedTeammates) => {
        const serializedValue = newSelectedTeammates
          ?.map((teammateOrCircle) => {
            if (isCirclePublicId(teammateOrCircle)) {
              return teammateOrCircle;
            }
            if (isNumber(teammateOrCircle)) {
              return serializeDataFieldsTableResourceValue(teammateOrCircle, 'teammate');
            }
            return undefined;
          })
          .filter(isDefined)
          .join(dataFieldsTableListValueSeparator);
        return onChange(serializedValue);
      }}
      shouldShowAllInitiallySelectedTeammates
    />
  );
};

function inboxToSearchValue(inbox: InboxModel): DataFieldsTableSearchValue {
  return {publicId: buildPublicId('inbox', inbox.id), searchValue: inbox.name};
}

function renderInboxValue(inbox: InboxModel) {
  return (
    <StyledResourceItemWrapper>
      <UserThemeConsumer>
        {(theme) => (
          <Icon name="inboxSmall" color={inboxHasColor(inbox) ? inbox.color : theme.greys.shade30} />
        )}
      </UserThemeConsumer>
      <StyledTextDiv>{inbox.name}</StyledTextDiv>
    </StyledResourceItemWrapper>
  );
}

function teammateOrCircleToSearchValue(teammateOrCircle: TeammateModel | CircleModel) {
  if ('email' in teammateOrCircle) {
    return {
      publicId: buildPublicId('teammate', teammateOrCircle.id),
      searchValue: buildTeammateDisplayName(teammateOrCircle),
    };
  }
  return {publicId: buildPublicId('circle', teammateOrCircle.id), searchValue: teammateOrCircle.name};
}

function renderTeammateOrCircleValue(
  teammateOrCircle: TeammateModel | CirclePublicId,
  circles: ReadonlyArray<CircleModel>,
) {
  if (isCirclePublicId(teammateOrCircle)) {
    const privateId = tryParsePublicId(teammateOrCircle, 'circle');
    const circle = circles.find((c) => c.id === privateId);
    return (
      <StyledResourceItemWrapper>
        <Icon name="groupOutlineSmall" />
        <StyledTextDiv>{circle?.name}</StyledTextDiv>
      </StyledResourceItemWrapper>
    );
  }

  return renderTeammateValue(teammateOrCircle);
}

function renderTeammateValue(teammate: TeammateModel) {
  return (
    <StyledResourceItemWrapper>
      <TeammateAvatar teammate={teammate} size={VisualSizesEnum.SMALL} />
      <StyledTextDiv>{buildTeammateDisplayName(teammate)}</StyledTextDiv>
    </StyledResourceItemWrapper>
  );
}

function tagToSearchValue(tag: TagModel): DataFieldsTableSearchValue {
  return {publicId: buildPublicId('tag', tag.id), searchValue: tag.name};
}

function renderEnumName(name: string) {
  return (
    <StyledResourceItemWrapper>
      <StyledTextDiv>{name}</StyledTextDiv>
    </StyledResourceItemWrapper>
  );
}

function renderTagValue(tag: TagModel) {
  return (
    <StyledResourceItemWrapper key={tag.id}>
      {renderTagIcon(tag)}
      <StyledTextDiv>{tag.name}</StyledTextDiv>
    </StyledResourceItemWrapper>
  );
}

function cannedAnswerToSearchValue(answer: AnswerModel): DataFieldsTableSearchValue {
  return {publicId: buildPublicId('cannedAnswer', answer.id), searchValue: answer.name};
}

function renderCannedAnswerValue(answer: AnswerModel) {
  return (
    <StyledResourceItemWrapper key={answer.id}>
      <StyledTextDiv>{answer.name}</StyledTextDiv>
    </StyledResourceItemWrapper>
  );
}

function overflowIndicatorRenderer(count: number) {
  return <StyledTextDiv>, +{count}</StyledTextDiv>;
}

/*
 * Constants.
 */

const CELL_RENDERERS: Record<DataFieldType, FC<DataFieldsTableCellProps>> = {
  [DataFieldType.BOOLEAN]: BooleanRenderer,
  [DataFieldType.CANNED_ANSWER]: CannedAnswerRenderer,
  [DataFieldType.INBOX]: InboxRenderer,
  [DataFieldType.INBOX_LIST]: InboxListRenderer,
  [DataFieldType.NUMBER]: NumberRenderer,
  [DataFieldType.STRING]: TextRenderer,
  [DataFieldType.TAG]: TagRenderer,
  [DataFieldType.TAG_LIST]: TagListRenderer,
  [DataFieldType.TEAMMATE]: TeammateRenderer,
  [DataFieldType.TEAMMATE_LIST]: TeammateListRenderer,
  [DataFieldType.LANGUAGE]: LanguageRenderer,
  [DataFieldType.DATETIME]: DatetimeRenderer,
};
